import React, { useEffect, useRef, useState } from "react";
import { Alert, AutoComplete, Button, Drawer, Form, Input, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Barcode from "components/shared-components/Barcode";
import { itemWeight, locationBarcode, normaliseLocationCode, validateLocationCode } from "functions/pickRoute";
import { SHOP_COLORS } from "../product-data/constants";

const { Text } = Typography;

/** Zones the plan names; any other text is allowed too. */
export const DEFAULT_ZONES = ["hardlopers", "stelling-links", "stelling-rechts", "pallets"];

/**
 * One vak: its code, zone and the text on its bordje.
 *
 * Built for entering a whole freezer (HVC-47, ~300 vakken) with the keyboard: the code field has
 * focus when the drawer opens, and Enter saves and moves on - to the next vak without a code when
 * editing, or to a fresh vak with the next code filled in when adding. The code is checked by the
 * hook against a fresh read before it is written; a clash comes back as a message here instead of
 * a second vak with the same code.
 *
 * @param {Object|null} entry - the entry being edited, null when adding a new vak
 * @param {Function} onSave - (values, { next }) => Promise<{ ok, message? }>
 */
const LocationDrawer = ({ open, entry, suggestedCode, zones, onClose, onSave, saving, hasNext }) => {
  const { t } = useTranslation();
  const [location, setLocation] = useState("");
  const [zone, setZone] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);
  const codeInput = useRef(null);
  const isNew = !entry;

  useEffect(() => {
    if (!open) return;
    setLocation(entry ? entry.location || "" : suggestedCode || "");
    setZone(entry ? entry.zone || "" : zone);
    setLabel(entry ? entry.label || "" : "");
    setError(null);
    // Focus after antd has mounted the drawer content
    const timer = setTimeout(() => codeInput.current && codeInput.current.focus({ cursor: "all" }), 60);
    return () => clearTimeout(timer);
    // `zone` is deliberately kept between two new vakken: they are usually in the same zone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry, suggestedCode]);

  const code = normaliseLocationCode(location);
  const codeValid = !code || validateLocationCode(code);

  const submit = async (next) => {
    if (!codeValid) {
      setError(t("pick_route.location_invalid"));
      return;
    }
    const result = await onSave({ location: code, zone, label }, { next });
    if (result && result.ok === false) setError(result.message);
  };

  const zoneOptions = [...new Set([...DEFAULT_ZONES, ...(zones || [])])].map((value) => ({ value }));
  const products = (entry && entry.products) || [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={440}
      destroyOnClose
      title={
        isNew
          ? t("pick_route.new_vak_title")
          : t("pick_route.edit_vak_title", { label: entry.label || entry.location || "" })
      }
      extra={
        <Space vertical>
          <Button onClick={() => submit(false)} loading={saving}>
            {t("save")}
          </Button>
          <Button type="primary" onClick={() => submit(true)} loading={saving}>
            {isNew ? t("pick_route.save_and_add_next") : hasNext ? t("pick_route.save_and_next") : t("save")}
          </Button>
        </Space>
      }
    >
      <p style={{ color: "#6b6b6b" }}>{isNew ? t("pick_route.new_vak_help") : t("pick_route.edit_vak_help")}</p>

      {!!error && <Alert type="error" showIcon className="mb-3" message={error} />}

      <Form layout="vertical" component={false}>
        <Form.Item
          label={t("pick_route.location")}
          validateStatus={codeValid ? undefined : "error"}
          help={codeValid ? t("pick_route.location_format") : t("pick_route.location_invalid")}
        >
          <Input
            ref={codeInput}
            value={location}
            maxLength={8}
            placeholder="1A"
            style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, textTransform: "uppercase" }}
            onChange={(event) => setLocation(event.target.value)}
            onPressEnter={() => submit(true)}
          />
        </Form.Item>

        <Form.Item label={t("pick_route.zone")}>
          <AutoComplete
            value={zone}
            options={zoneOptions}
            onChange={setZone}
            placeholder={t("pick_route.zone_placeholder")}
            filterOption={(input, option) => option.value.toLowerCase().includes((input || "").toLowerCase())}
          >
            <Input onPressEnter={() => submit(true)} />
          </AutoComplete>
        </Form.Item>

        <Form.Item label={t("pick_route.sign_text")} help={t("pick_route.sign_text_help")}>
          <Input
            value={label}
            placeholder={t("pick_route.sign_text_placeholder")}
            onChange={(event) => setLabel(event.target.value)}
            onPressEnter={() => submit(true)}
          />
        </Form.Item>
      </Form>

      {!!code && codeValid && (
        <div className="mb-3">
          <Text type="secondary">{t("pick_route.barcode_preview")}</Text>
          <div style={{ marginTop: 6 }}>
            <Barcode value={locationBarcode(code)} moduleMm={0.35} heightMm={10} showValue />
          </div>
        </div>
      )}

      {!isNew && (
        <div className="mb-3">
          <Text type="secondary">{t("pick_route.linked_products")}</Text>
          <div style={{ marginTop: 6 }}>
            {products.length === 0 ? (
              <Tag color="red">{t("pick_route.no_products")}</Tag>
            ) : (
              <Space size={4} wrap>
                {products.map((product) => {
                  const weight = itemWeight(product);
                  return (
                    <Tag
                      key={`${product.shopSource}|${product.productId}|${weight}`}
                      color={SHOP_COLORS[product.shopSource] || "default"}
                    >
                      {product.shopSource} · {product.name || product.productId}
                      {weight !== null ? ` · ${weight} g` : ""}
                    </Tag>
                  );
                })}
              </Space>
            )}
          </div>
        </div>
      )}

      {!isNew && !!entry.updatedAt && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("pick_route.last_changed", {
            when: dayjs(entry.updatedAt < 1e12 ? entry.updatedAt * 1000 : entry.updatedAt).format("D MMM YYYY HH:mm"),
            who: entry.updatedBy || "-",
          })}
        </Text>
      )}
    </Drawer>
  );
};

export default LocationDrawer;
